import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Container, Card, Table, Button, Badge, Spinner, Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import InformacionUsuario from "../modales/InformacionUsuario";
import FotoINE from "../modales/FotoINE";
import EditSolicitudModal from "../modales/EditSolicitudModal";
import RejectReasonModal from "../modales/RejectReasonModal";

import {API_BASE} from "../../services/apirest";

export default function AdminSolicitudes() {
  // Inicializamos list como array vacío
  const [list, setList] = useState([]);
  const [rolId, setRolId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [perfil, setPerfil] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [editData, setEditData] = useState({});
  const [editErrors, setEditErrors] = useState({ rfc: "", nombre: "" });
  const [isRFCDuplicateEdit, setIsRFCDuplicateEdit] = useState(false);
  const [isBlacklistedEdit, setIsBlacklistedEdit] = useState(false)
  const [loading, setLoading] = useState(false);
  const validarRFCsinHomoclave = (rfc) => /^[A-ZÑ&]{3,4}\d{6}$/i.test(rfc);
  // Estados para filtros
  const [searchTelefono] = useState("");
  const [searchRFC] = useState("");
  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  }), []);

  const ultimosRoles = [
    { id: 8, nombre:  "VENDEDOR (ACTIVE DIRECTORY/SIEBEL TELCOM)" },
    { id: 9, nombre:  "VENDEDOR / INSTALADOR (TOA)" },
    { id: 10, nombre: "SUPERVISOR" },
    { id: 11, nombre: "ADMINISTRATIVO"},
];

  // Carga perfil
  useEffect(() => {
    fetch(`${API_BASE}/perfil.php`, { headers })
        .then(r => r.json())
        .then(d => {
          if (d.status === "success") {
            setPerfil(d.result.user);
          }
        })
        .catch(console.error);
  }, [headers]);

  // Carga rol_id
  useEffect(() => {
    const r = localStorage.getItem("rol_id");
    setRolId(r ? parseInt(r, 10) : null);
  }, []);

  // Función de carga
  const load = useCallback(async () => {
    if (!perfil) return;
    try {
      const res = await fetch(`${API_BASE}/solicitudes.php`, { headers });
      const data = await res.json();
      let todas = data.status === "success" ? data.result.solicitudes : [];

      // Si soy supervisor (rol 3) filtro por mi email
      if (perfil.rol_id === 3) {
        const first = (...cands) => cands.find(v => v !== undefined && v !== null && v !== "");
        const norm = (v) => (v ? String(v).trim().toLowerCase() : "");
        const mySup = norm(first(perfil.correo_supervisor, perfil.Usuario, perfil.usuario, perfil.email));
        if (!mySup) {
          todas = [];
        } else {
          todas = todas.filter(s => {
            const sup = norm(first(s.correo_supervisor, s.supervisor_correo, s.supervisor_email));
            return sup === mySup;
          });
        }
      }
      setList(todas);
    } catch (err) {
      console.error(err);
      setList([]);
    }
  }, [headers, perfil]);
  // Ejecuta load al montar y cada 30s
  useEffect(() => {
    load();
    const poll = setInterval(load, 30000);
    return () => clearInterval(poll);
  }, [load]);

  const visibleList = useMemo(() => {
    const arr = Array.isArray(list) ? list : [];
    const esReactivacion = (s) => !!(s.reactivacion_stage && String(s.reactivacion_stage).trim() !== "");

    if (![1,2,3].includes(Number(rolId))) return [];

    const soloPendientesNoReactivacion = arr.filter(s => !esReactivacion(s));
    const porTelefono = searchTelefono
        ? soloPendientesNoReactivacion.filter(s => (s.telefono || "").includes(searchTelefono))
        : soloPendientesNoReactivacion;
    const porRFC = searchRFC
        ? porTelefono.filter(s => (s.rfc || "").toUpperCase().includes(searchRFC.toUpperCase()))
        : porTelefono;
    return [...porRFC].sort((a,b) => new Date(b.Fecha) - new Date(a.Fecha));
  }, [list, rolId, searchTelefono, searchRFC]);


  const validateEditRFC = async () => {
    const rfc = (editData.rfc || "").trim().toUpperCase();
    // 1) Formato
    if (!validarRFCsinHomoclave(rfc)) {
      setEditErrors(e => ({ ...e, rfc: "Formato inválido: 3–4 letras + 6 dígitos" }));
      return false;
    }
    // 2) Si es el mismo que había, no lo volvemos a checar
    if (rfc === editData.originalRfc) {
      setEditErrors(e => ({ ...e, rfc: "" }));
      setIsRFCDuplicateEdit(false);
      return true;
    }
    // 3) Duplicado solo si cambió
    try {
      const res = await fetch(
          `${API_BASE}/checkRegistro.php?campo=rfc&valor=${encodeURIComponent(rfc)}`
      );
      const chk = await res.json();
      if (
          chk.status === "ok" &&
          Array.isArray(chk.result) &&
          chk.result[0]?.status === "Activo"
      ) {
        setEditErrors(e => ({ ...e, rfc: "Este RFC ya está registrado" }));
        setIsRFCDuplicateEdit(true);
        return false;
      }
    } catch {
      // opcional: manejar error de red
    }
    // todo OK
    setEditErrors(e => ({ ...e, rfc: "" }));
    setIsRFCDuplicateEdit(false);
    setEditData(d => ({ ...d, rfc }));
    return true;
  };

  const validateEditNombre = async () => {
    const fullName = `${editData.nombre_usuario} ${editData.apellido_paterno} ${editData.apellido_materno}`
        .trim()
        .replace(/\s+/g, " ");  // quita espacios extra
    if (!fullName) {
      setEditErrors(e => ({ ...e, nombre: "El nombre no puede estar vacío" }));
      return false;
    }
    try {
      const res = await fetch(`${API_BASE}/blackList.php?nombre=${encodeURIComponent(fullName)}`);
      const data = await res.json();
      if (data.status === "ok" && Array.isArray(data.result) && data.result.length > 0) {
        setEditErrors(e => ({ ...e, nombre: "Este usuario está en lista negra" }));
        setIsBlacklistedEdit(true);
        return false;
      }
    } catch {
      // opcional: manejar error de red
    }
    setEditErrors(e => ({ ...e, nombre: "" }));
    setIsBlacklistedEdit(false);
    // opcional: normalizar nombre en mayúsculas y espacios simples
    setEditData(d => ({ ...d, nombre_usuario: fullName.split(" ")[0], apellido_paterno: fullName.split(" ")[1]||"", apellido_materno: fullName.split(" ")[2]||"" }));
    return true;
  };

  const onChangeField = (name, value) => setEditData(d => ({ ...d, [name]: value }));

  const handleEditClick = (s) => {
    setSelected(s);
    setEditData({
      SolicitudId: s.SolicitudId,
      originalRfc: s.rfc,
      usuario: s.usuario,
      nombre_usuario: s.nombre_usuario,
      apellido_paterno: s.apellido_paterno,
      apellido_materno: s.apellido_materno,
      rfc: s.rfc,
      numero_vendedor: s.numero_vendedor,
      telefono: s.telefono,
      foto_url: s.foto_url,
      supervisor_distribuidor: s.supervisor_distribuidor || "",
      rfc_supervisor_distribuidor: s.rfc_supervisor_distribuidor || "",
      clave_supervisor: s.clave_supervisor || ""
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    // espera ambas validaciones
    const okRFC    = await validateEditRFC();
    const okNombre = await validateEditNombre();
    if (!okRFC || !okNombre) {
      return; // sale sin enviar
    }
    // aquí ya puedes llamar al PUT normal
    try {
      const res = await fetch(`${API_BASE}/solicitudes.php`, {
        method: "PUT",
        headers,
        body: JSON.stringify(editData),
      });
      const d = await res.json();
      if (d.status === "success" || d.status === "ok") {
        Swal.fire("¡LISTO!", "Los cambios fueron actualizados", "success");
        window.dispatchEvent(new Event("solicitudes:refresh"));
        window.dispatchEvent(new Event("inbox:refresh"));
        setShowEditModal(false);
        load();
      } else {
        Swal.fire("Error", d.result?.error_msg || "No se pudo guardar", "error");
      }
    } catch {
      Swal.fire("Error", "Fallo en la petición", "error");
    }
  };

  const confirmReject = async () => {
    try {
      const res = await fetch(`${API_BASE}/solicitudes.php`, {
        method: "DELETE",
        headers,
        body: JSON.stringify({
          SolicitudId: selected.SolicitudId,
          situacion_rechazo: rejectReason
        })
      });
      const d = await res.json();
      if (d.status === "success" || d.status === "ok") {
        Swal.fire("¡LISTO!", "Solicitud rechazada", "success");
        window.dispatchEvent(new Event("solicitudes:refresh"));
        window.dispatchEvent(new Event("inbox:refresh"));
        load();
      } else {
        Swal.fire("Error", d.result?.error_msg || "Algo falló", "error");
      }
    } catch {
      Swal.fire("Error", "No se pudo procesar", "error");
    } finally {
      setShowRejectModal(false);
    }
  };

  const exportToExcel = () => {
    if (visibleList.length === 0) return;
    const headersCsv = ["DISTRIBUIDOR","NOMBRE COMPLETO","TELÉFONO","FECHA", "RFC"];
    const rows = visibleList.map(s => [
      s.distribuidor_nombre,
      `${s.nombre_usuario} ${s.apellido_paterno} ${s.apellido_materno}`.trim(),
      s.telefono,
      s.Fecha,
      s.rfc,
    ]);
    const csv = [
      headersCsv.join(","),
      ...rows.map(r => r.map(cell => `"${cell}"`).join(","))
    ].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "solicitudes_export.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
      <div className="app-container">
        <Container className="my-5">
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Solicitudes de Usuario</h5>
              <Badge bg={visibleList.length ? "danger" : "secondary"} pill>
                {visibleList.length}
              </Badge>


            </Card.Header>
            <Card.Body>
              <div className="mb-3 d-flex align-items-end flex-wrap">
                <Button
                    variant="success"
                    onClick={exportToExcel}
                    className="mb-2"
                >
                  Exportar
                </Button>
              </div>

              {visibleList.length === 0 ? (
                  <p className="text-center text-muted mb-0">No hay solicitudes pendientes.</p>
              ) : (
                      <Table responsive hover striped className="mb-0 align-middle">
                        <thead>
                        <tr>
                          <th>Distribuidor</th>
                          <th>Nombre Completo</th>
                          <th>Teléfono</th>
                          <th>Fecha</th>
                          <th>RFC</th>
                          <th className="text-center">Acciones</th>
                        </tr>
                        </thead>
                        <tbody>
                        {visibleList.map(s => (
                            <tr key={s.SolicitudId}>
                              <td>{s.distribuidor_nombre}</td>
                              <td>{`${s.nombre_usuario} ${s.apellido_paterno} ${s.apellido_materno}`}</td>
                              <td>{s.telefono}</td>
                              <td>{new Date(s.Fecha).toLocaleDateString()}</td>
                              <td>{s.rfc}</td>
                              <td className="text-center">
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => {
                                      setSelected(s);
                                      setShowDetailModal(true);
                                    }}
                                >
                                  Validar
                                </Button>
                              </td>
                            </tr>
                        ))}
                        </tbody>
                      </Table>
                  )}
            </Card.Body>
          </Card>
        </Container>
        {/* Modal Detalle */}
        <Modal
            show={showDetailModal}
            onHide={() => setShowDetailModal(false)}
            size="lg"
            centered
            dialogClassName="modal-100w"
            style={{ zIndex: 11000 }}
        >
          <Modal.Header closeButton>
            <Modal.Title>Detalle de Solicitud </Modal.Title>
          </Modal.Header>
          {selected && (
              <Modal.Body>

                {/* Card 1: Distribuidor */}
                <InformacionUsuario
                    user={selected}
                    title="Distribuidor"
                    cols={2}
                    fields={[
                      { key: "distribuidor_nombre", label: "Distribuidor" },
                      { key: "distribuidor_rs",     label: "Razón Social" },
                    ]}
                />
                {/* Card 2: Datos del usuario / solicitud */}
                <InformacionUsuario
                    user={selected}
                    title="Datos del usuario"
                    cols={3}
                    fields={[
                      { key: "usuario",          label: "Email" }, // ojo: es selected.usuario
                      {
                        key: "nombre",
                        label: "Nombre",
                        render: (u) => [u?.nombre_usuario, u?.apellido_paterno, u?.apellido_materno]
                            .filter(Boolean)
                            .join(" "),
                      },
                      { key: "rfc",              label: "RFC", render: (u) => (u?.rfc || "").toUpperCase() },
                      { key: "telefono",         label: "Teléfono" },
                      { key: "Fecha",            label: "Fecha de Solicitud", render: (u) => new Date(u?.Fecha).toLocaleDateString("es-MX") },
                      { key: "numero_vendedor",  label: "Número Empleado" },
                      {
                        key: "rol_id",
                        label: "Puesto actual",
                        render: (u) => {
                          const id = Number(u?.rol_id);
                          return ultimosRoles.find(r => r.id === id)?.nombre ?? "-";
                        }
          },
                      
                    ]}
                />
                  {/* 5) Bodega (SOLO si hay datos) */}
                    {(
                      selected?.bodega_distribuidor ||
                      selected?.bodega_almacen ||
                      selected?.bodega_nombre ||
                      selected?.bodega_region ||
                      selected?.bodega_direccion
                    ) && (
                      <InformacionUsuario
                        user={selected}
                        title="Bodega"
                        cols={2}
                        hideEmpty={true}
                        fields={[
                          { key: "bodega_distribuidor", label: "Distribuidor" },
                          { key: "bodega_almacen",      label: "Almacén" },
                          { key: "bodega_nombre",       label: "Bodega" },
                          { key: "bodega_region",       label: "Región" },
                          { key: "bodega_direccion",    label: "Dirección" },
                        ]}
                      />
                    )}

                {/* Card 3: Ubicación */}
                <InformacionUsuario
                    user={selected}
                    title="Ubicación"
                    cols={3}
                    fields={[
                      { key: "region_hub",    label: "Hub" },
                      { key: "region_nombre", label: "Región" },
                      { key: "region_plaza",  label: "Plaza" },
                    ]}
                />
                <hr />
                <p><strong>INE:</strong></p>
                {selected.ine_url ? (
                   <FotoINE url={selected?.ine_url}/>
                ) : (
                    <p className="text-muted">No disponible</p>
                )}
              </Modal.Body>
          )}
          <Modal.Footer>
            {selected && ![1, 2].includes(rolId) && (
                <div>
                  {/* Botón Autorizar */}
                  <Button
                      variant="success"
                      size="sm"
                      className="me-2"
                      onClick={() => {
                        setShowDetailModal(false);
                        setShowUploadModal(true);
                      }}
                  >
                    Autorizar
                  </Button>
                  {/* Botón Editar */}
                  {[1, 2, 3].includes(rolId) && (
                      <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => {
                            handleEditClick(selected);
                            setShowDetailModal(false); // cerramos detalle al abrir edición
                          }}
                      >
                        Editar
                      </Button>
                  )}
                  {/* Botón Rechazar */}
                  <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => {
                        setShowRejectModal(true);
                        setShowDetailModal(false); // cerramos detalle al abrir rechazo
                      }}
                  >
                    Rechazar
                  </Button>
                </div>
            )}
            <Button variant="secondary" size="sm" onClick={() => setShowDetailModal(false)}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal Subida de Foto */}
        <Modal show={showUploadModal} onHide={() => { setShowUploadModal(false); setUploadFile(null); }} centered>
          <Modal.Header closeButton><Modal.Title>Subir foto para continuar</Modal.Title></Modal.Header>
          <Modal.Body>
            <input type="file" accept="image/*" onChange={e => setUploadFile(e.target.files[0])}/>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowUploadModal(false); setUploadFile(null); }}>Cancelar</Button>
           <Button
              variant="primary"
              disabled={!uploadFile || loading}
              onClick={async () => {
                const token = localStorage.getItem("token");
                const formData = new FormData();
                formData.append("SolicitudId", selected.SolicitudId);
                formData.append("foto", uploadFile);

                try {
                  setLoading(true);

                  const res = await fetch(`${API_BASE}/solicitudes.php`, {
                    method: "POST",
                    headers: {
                      Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                  });

                  const d = await res.json();

                  if (d.status === "success" || d.status === "ok") {
                    Swal.fire("¡LISTO!", "Solicitud Enviada", "success");
                    window.dispatchEvent(new Event("solicitudes:refresh"));
                    window.dispatchEvent(new Event("inbox:refresh"));
                    load();
                  } else {
                    Swal.fire("Error", d.result?.error_msg || "Algo falló", "error");
                  }
                } catch (err) {
                  Swal.fire("Error", "No se pudo procesar", "error");
                } finally {
                  setLoading(false);
                  setShowUploadModal(false);
                  setUploadFile(null);
                }
              }}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Enviando...
                </>
              ) : (
                "Subir y Continuar"
              )}
            </Button>
          </Modal.Footer>
        </Modal>
        {/* Modal Editar Solicitud */}
        <EditSolicitudModal
            open={showEditModal}
            onClose={() => setShowEditModal(false)}
            onSave={handleEditSave}
            data={editData}
            errors={editErrors}
            onChangeField={onChangeField}
            onBlurNombre={validateEditNombre}
            onBlurRFC={validateEditRFC}
            disableSave={isRFCDuplicateEdit || isBlacklistedEdit}
        />
        {/* Modal Rechazar Solicitud */}
        <RejectReasonModal
            open={showRejectModal}
            onClose={() => setShowRejectModal(false)}
            onConfirm={confirmReject}
            reason={rejectReason}
            onChangeReason={setRejectReason}
        />
      </div>
  );
}